// src/App.tsx
// @ts-nocheck - Disable TypeScript checking for this file

import React from 'react';
import { NavigationProviderWrapper, useNavigation } from '@/context/NavigationContext'; // Используем обновленный провайдер
import { AuthProvider, useAuthContext } from '@/context/AuthContext'; 
import { useTranslation } from 'react-i18next';
import Layout from '@/components/layout/Layout';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

// Динамический импорт страниц
const HomePage = React.lazy(() => import('@/pages/HomePage'));
const OAuthCallback = React.lazy(() => import('@/components/auth/OAuthCallback'));
const PasswordResetPage = React.lazy(() => import('@/pages/personal/PasswordResetPage'));
const UserProfilePage = React.lazy(() => import('@/pages/personal/UserProfilePage'));
const UserSettingsPage = React.lazy(() => import('@/pages/personal/UserSettingsPage'));

// Импорт PersonalLayout
import PersonalLayout from '@/pages/PersonalLayout';

// Импорт страниц настроек
import ApplicationSettingsPage from '@/pages/personal/settings/ApplicationPage';
import PlatformSettingsPage from '@/pages/personal/settings/PlatformPage';
import PromotionsDiscountsPage from '@/pages/personal/settings/PromotionsDiscountsPage';

// Импорт страниц платформ
import WhatsAppPage from '@/pages/personal/platforms/WhatsAppPage';
import InstagramPage from '@/pages/personal/platforms/InstagramPage';
import TelegramPage from '@/pages/personal/platforms/TelegramPage';
import MessengerPage from '@/pages/personal/platforms/MessengerPage';
import BroadcastsPage from '@/pages/personal/platforms/BroadcastsPage';
import OrdersPage from '@/pages/personal/platforms/OrdersPage';
import ClientsPage from '@/pages/personal/platforms/ClientsPage';

// Импорт страниц аналитики
import DashboardPage from '@/pages/personal/analytics/DashboardPage';
import CustomerPage from '@/pages/personal/analytics/CustomerPage';
// Удалены импорты SalesPage и MarketingPage
import FeedbackPage from '@/pages/personal/analytics/FeedbackPage';
import FunnelPage from '@/pages/personal/analytics/FunnelPage';
// Импорт новой страницы маркетинговых расходов
import MarketingExpensesPage from '@/pages/personal/analytics/MarketingExpensesPage';

// Определение поддерживаемых языков
const SUPPORTED_LANGUAGES = ['en', 'ru'] as const;
type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

// Функция определения языка браузера
const getBrowserLanguage = (): SupportedLanguage => {
  const defaultLang = 'en' as SupportedLanguage;
  
  try {
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const browserLang = localStorage.getItem('i18nextLng') || defaultLang;
      return SUPPORTED_LANGUAGES.includes(browserLang as SupportedLanguage)
        ? (browserLang as SupportedLanguage)
        : defaultLang;
    }
  } catch {
    return defaultLang;
  }
  
  return defaultLang;
};

// Компонент для защищенных маршрутов
const ProtectedRoute: React.FC<{ element: React.ReactElement }> = ({ element }) => {
  const { isAuthenticated, isLoading } = useAuthContext();
  
  // Если авторизация все еще проверяется, отображаем загрузчик
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  // Если пользователь не авторизован, перенаправляем на главную
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  // Если пользователь авторизован, отображаем запрошенный компонент
  return element;
};

// Основной компонент содержимого приложения
const AppContent: React.FC = () => {
  const { isAuthenticated } = useAuthContext();
  
  return (
    <React.Suspense fallback={
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    }>
      <Routes>
        <Route element={<Layout />}>
          {/* Публичные маршруты */}
          <Route path="/oauth/callback" element={<OAuthCallback />} />
          <Route path="/reset-password/:token" element={<PasswordResetPage />} />
          <Route path="/" element={
            isAuthenticated 
              ? <Navigate to="/personal/platforms/whatsapp" replace /> 
              : <HomePage />
          } />

          {/* Личный кабинет теперь внутри Layout */}
          <Route path="/personal" element={<ProtectedRoute element={<PersonalLayout />} />}>
            {/* Редирект с корневого пути на платформу WhatsApp */}
            <Route index element={<Navigate to="platforms/whatsapp" replace />} />
            
            {/* Профиль и настройки размещены внутри PersonalLayout для единообразия */}
            <Route path="profile" element={<UserProfilePage />} />
            <Route path="user-settings" element={<UserSettingsPage />} />
            
            {/* Настройки */}
            <Route path="settings">
              <Route index element={<Navigate to="application" replace />} />
              <Route path="application" element={<ApplicationSettingsPage />} />
              <Route path="platform" element={<PlatformSettingsPage />} />
              <Route path="promotions" element={<PromotionsDiscountsPage />} />
            </Route>
            
            {/* Платформы */}
            <Route path="platforms">
              <Route index element={<Navigate to="whatsapp" replace />} />
              <Route path="whatsapp" element={<WhatsAppPage />} />
              <Route path="instagram" element={<InstagramPage />} />
              <Route path="telegram" element={<TelegramPage />} />
              <Route path="messenger" element={<MessengerPage />} />
              <Route path="broadcasts" element={<BroadcastsPage />} />
              <Route path="orders" element={<OrdersPage />} />
              <Route path="clients" element={<ClientsPage />} />
            </Route>
            
            {/* Аналитика */}
            <Route path="analytics">
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />
              {/* Удалены маршруты sales и marketing */}
              <Route path="customer" element={<CustomerPage />} />
              <Route path="feedback" element={<FeedbackPage />} />
              <Route path="funnel" element={<FunnelPage />} />
              {/* Добавляем новый маршрут для маркетинговых расходов */}
              <Route path="marketing-expenses" element={<MarketingExpensesPage />} />
            </Route>
          </Route>
          
          {/* Обработка несуществующих маршрутов */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </React.Suspense>
  );
};

// Корневой компонент приложения
const App: React.FC = () => {
  const { i18n } = useTranslation();
  
  // Обновленная логика определения basename
  const detectBasePath = () => {
    if (typeof window === 'undefined') return '/';
    
    const path = window.location.pathname;
    if (path.includes('/pinnax-app')) return '/pinnax-app';
    if (path.includes('/rinnax-app')) return '/rinnax-app';
    
    return '/'; // Для локальной разработки без подпути
  };
  
  const basename = detectBasePath();
  
  console.log("App initialized with basename:", basename);
  console.log("Current environment:", import.meta.env.MODE);
  console.log("Current pathname:", typeof window !== 'undefined' ? window.location.pathname : '');

  // Определяем начальный язык при загрузке приложения
  React.useEffect(() => {
    const detectedLang = getBrowserLanguage();
    i18n.changeLanguage(detectedLang);
    console.log("Language set to:", detectedLang);
  }, [i18n]);

  return (
    <BrowserRouter basename={basename}>
      <AuthProvider>
        {/* Используем NavigationProviderWrapper вместо NavigationProvider */}
        <NavigationProviderWrapper>
          <AppContent />
        </NavigationProviderWrapper>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;